import {
  Badge,
  Box,
  Button,
  HStack,
  Icon,
  Input,
  Text,
  VStack
} from "@chakra-ui/react";
import React, { useState } from "react";
import {
  FiCheck,
  FiChevronDown,
  FiPlus,
  FiRefreshCw,
  FiSearch,
  FiTrash2
} from "react-icons/fi";

interface ABTestPath {
  id: string;
  type: "path" | "variant";
  value: string;
  percentage: number;
}

interface ABTestPathsProps {
  paths: ABTestPath[];
  onChange: (paths: ABTestPath[]) => void;
  disabled?: boolean;
  domain: string;
  availablePaths: any[];
  availableVariants: any[];
  dropdownStates: {
    [key: string]: { isOpen: boolean; searchQuery: string };
  };
  onDropdownChange: (
    pathId: string,
    state: { isOpen: boolean; searchQuery: string }
  ) => void;
}

export const ABTestPaths: React.FC<ABTestPathsProps> = ({
  paths,
  onChange,
  disabled = false,
  domain,
  availablePaths,
  availableVariants,
  dropdownStates,
  onDropdownChange
}) => {
  const [refreshingStates, setRefreshingStates] = useState<{ [key: string]: boolean }>({});

  const getMainDomain = (trackingDomain: string) => {
    if (trackingDomain.includes("zown.ca")) {
      return "zown.ca";
    } else if (trackingDomain.includes("zownhomes.com")) {
      return "zownhomes.com";
    }
    return "zownhomes.com";
  };

  const addPath = () => {
    const newPath = {
      id: Date.now().toString(),
      type: "path" as const,
      value: "",
      percentage: 0
    };
    const updatedPaths = [...paths, newPath];
    const redistributedPaths = updatedPaths.map((p) => ({
      ...p,
      percentage: Math.floor(100 / updatedPaths.length)
    }));
    onChange(redistributedPaths);
  };

  const removePath = (pathId: string) => {
    const updatedPaths = paths.filter((p) => p.id !== pathId);
    const redistributedPaths = updatedPaths.map((p) => ({
      ...p,
      percentage: Math.floor(100 / updatedPaths.length)
    }));
    onChange(redistributedPaths);
  };

  const updatePath = (index: number, updates: Partial<ABTestPath>) => {
    const updatedPaths = [...paths];
    updatedPaths[index] = { ...updatedPaths[index], ...updates };
    onChange(updatedPaths);
  };

  const distributeEvenly = () => {
    const equalPercentage = Math.floor(100 / paths.length);
    const remainder = 100 % paths.length;
    const redistributedPaths = paths.map((p, i) => ({
      ...p,
      percentage: i === 0 ? equalPercentage + remainder : equalPercentage
    }));
    onChange(redistributedPaths);
  };

  const totalPercentage = paths.reduce((sum, p) => sum + p.percentage, 0);

  return (
    <Box>
      <HStack mb={3} justify="space-between">
        <Text fontSize="sm" fontWeight="medium">
          Test Paths
        </Text>
        <Button
          type="button"
          size="xs"
          colorScheme="blue"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            addPath();
          }}
          disabled={disabled}
        >
          <HStack gap={1}>
            <Icon as={FiPlus} />
            <Text>Add Path</Text>
          </HStack>
        </Button>
      </HStack>

      <VStack gap={3} align="stretch">
        {paths.map((path, index) => {
          const dropdown = dropdownStates[path.id] || {
            isOpen: false,
            searchQuery: ""
          };
          const filteredPaths = availablePaths.filter(
            (p) =>
              p.value
                .toLowerCase()
                .includes(dropdown.searchQuery.toLowerCase()) ||
              p.description
                .toLowerCase()
                .includes(dropdown.searchQuery.toLowerCase())
          );
          const filteredVariants = availableVariants.filter(
            (v) =>
              v.value
                .toLowerCase()
                .includes(dropdown.searchQuery.toLowerCase()) ||
              v.description
                .toLowerCase()
                .includes(dropdown.searchQuery.toLowerCase())
          );

          return (
            <Box
              key={path.id}
              p={3}
              bg="gray.50"
              rounded="md"
              border="1px"
              borderColor="gray.200"
            >
              <HStack mb={2} justify="space-between">
                <HStack>
                  <Text fontSize="sm" fontWeight="medium">
                    Path {String.fromCharCode(65 + index)}
                  </Text>
                  <Badge
                    colorScheme={
                      ["green", "orange", "blue", "purple"][index % 4]
                    }
                    size="sm"
                  >
                    {path.percentage}%
                  </Badge>
                </HStack>
                {paths.length > 2 && (
                  <Button
                    type="button"
                    size="xs"
                    variant="ghost"
                    colorScheme="red"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      removePath(path.id);
                    }}
                    disabled={disabled}
                  >
                    <Icon as={FiTrash2} />
                  </Button>
                )}
              </HStack>

              <HStack mb={2}>
                <Button
                  type="button"
                  size="xs"
                  variant={path.type === "path" ? "solid" : "outline"}
                  colorScheme="gray"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    updatePath(index, { type: "path", value: "" });
                  }}
                  disabled={disabled}
                >
                  Path
                </Button>
                <Button
                  type="button"
                  size="xs"
                  variant={path.type === "variant" ? "solid" : "outline"}
                  colorScheme="gray"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    updatePath(index, { type: "variant", value: "" });
                  }}
                  disabled={disabled}
                >
                  Variant
                </Button>
              </HStack>

              {path.type === "path" ? (
                <HStack>
                  <Text fontSize="sm" color="gray.600" minW="fit-content">
                    https://{getMainDomain(domain)}
                  </Text>
                  <Box position="relative" flex={1} data-ab-dropdown={path.id}>
                    <Box
                      as="button"
                      w="100%"
                      p={2}
                      bg="white"
                      border="1px solid"
                      borderColor={dropdown.isOpen ? "blue.500" : "gray.300"}
                      borderRadius="md"
                      textAlign="left"
                      fontSize="sm"
                      aria-disabled={disabled}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (!disabled) {
                          onDropdownChange(path.id, {
                            isOpen: !dropdown.isOpen,
                            searchQuery: ""
                          });
                        }
                      }}
                      _hover={{ borderColor: "gray.400" }}
                      _focus={{
                        borderColor: "blue.500",
                        boxShadow: "0 0 0 1px var(--chakra-colors-blue-500)",
                        outline: "none"
                      }}
                      _disabled={{
                        opacity: 0.6,
                        cursor: "not-allowed"
                      }}
                    >
                      <HStack justify="space-between">
                        <Text
                          color={path.value ? "black" : "gray.500"}
                          fontSize="sm"
                        >
                          {path.value || "Select a path..."}
                        </Text>
                        <Icon
                          as={FiChevronDown}
                          transform={
                            dropdown.isOpen ? "rotate(180deg)" : "rotate(0deg)"
                          }
                          transition="transform 0.2s"
                        />
                      </HStack>
                    </Box>

                    {dropdown.isOpen && (
                      <Box
                        position="absolute"
                        top="100%"
                        left={0}
                        right={0}
                        mt={1}
                        bg="white"
                        border="1px solid"
                        borderColor="gray.200"
                        borderRadius="md"
                        shadow="lg"
                        zIndex={10}
                        maxH="300px"
                        overflow="hidden"
                      >
                        <Box
                          p={2}
                          borderBottom="1px solid"
                          borderColor="gray.200"
                        >
                          <HStack>
                            <Icon as={FiSearch} color="gray.400" />
                            <Input
                              placeholder="Search paths..."
                              value={dropdown.searchQuery}
                              onChange={(e) =>
                                onDropdownChange(path.id, {
                                  ...dropdown,
                                  searchQuery: e.target.value
                                })
                              }
                              size="sm"
                              border="none"
                              _focus={{
                                outline: "none",
                                boxShadow: "none"
                              }}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </HStack>
                        </Box>

                        <Box maxH="250px" overflowY="auto">
                          {filteredPaths.map((availPath) => (
                            <Box
                              key={availPath.value}
                              as="button"
                              w="100%"
                              p={2}
                              textAlign="left"
                              bg={
                                path.value === availPath.value
                                  ? "blue.50"
                                  : "white"
                              }
                              _hover={{ bg: "gray.50" }}
                              onClick={(e) => {
                                e.stopPropagation();
                                updatePath(index, { value: availPath.value });
                                onDropdownChange(path.id, {
                                  isOpen: false,
                                  searchQuery: ""
                                });
                              }}
                              borderBottom="1px solid"
                              borderColor="gray.100"
                            >
                              <HStack justify="space-between" align="start">
                                <VStack align="start" gap={0}>
                                  <Text
                                    fontSize="sm"
                                    fontWeight={
                                      path.value === availPath.value
                                        ? "medium"
                                        : "normal"
                                    }
                                  >
                                    {availPath.label}
                                  </Text>
                                  <Text fontSize="xs" color="gray.500">
                                    {availPath.description}
                                  </Text>
                                </VStack>
                                {path.value === availPath.value && (
                                  <Icon as={FiCheck} color="blue.500" />
                                )}
                              </HStack>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    )}
                  </Box>
                  <Button
                    size="xs"
                    variant="ghost"
                    onClick={() => {
                      const refreshKey = `${path.id}-path`;
                      setRefreshingStates(prev => ({ ...prev, [refreshKey]: true }));
                      // Reset search and close dropdown to refresh the list
                      onDropdownChange(path.id, {
                        isOpen: false,
                        searchQuery: ""
                      });
                      // Small delay then reopen to show refreshed list
                      setTimeout(() => {
                        onDropdownChange(path.id, {
                          isOpen: true,
                          searchQuery: ""
                        });
                        setRefreshingStates(prev => ({ ...prev, [refreshKey]: false }));
                      }, 500);
                    }}
                    disabled={disabled || refreshingStates[`${path.id}-path`]}
                    aria-label="Refresh path list"
                    title="Refresh path list"
                    px={2}
                    _hover={{ bg: "gray.100" }}
                  >
                    <Icon 
                      as={FiRefreshCw} 
                      boxSize={3} 
                      className={refreshingStates[`${path.id}-path`] ? "animate-spin" : ""} 
                    />
                  </Button>
                </HStack>
              ) : (
                <HStack>
                  <Box position="relative" flex={1} data-ab-dropdown={path.id}>
                  <Box
                    as="button"
                    w="100%"
                    p={2}
                    bg="white"
                    border="1px solid"
                    borderColor={dropdown.isOpen ? "blue.500" : "gray.300"}
                    borderRadius="md"
                    textAlign="left"
                    fontSize="sm"
                    aria-disabled={disabled}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (!disabled) {
                        onDropdownChange(path.id, {
                          isOpen: !dropdown.isOpen,
                          searchQuery: ""
                        });
                      }
                    }}
                    _hover={{ borderColor: "gray.400" }}
                    _focus={{
                      borderColor: "blue.500",
                      boxShadow: "0 0 0 1px var(--chakra-colors-blue-500)",
                      outline: "none"
                    }}
                    _disabled={{
                      opacity: 0.6,
                      cursor: "not-allowed"
                    }}
                  >
                    <HStack justify="space-between">
                      <Text
                        color={path.value ? "black" : "gray.500"}
                        fontSize="sm"
                        fontFamily={path.value ? "mono" : "inherit"}
                      >
                        {path.value || "Select a variant..."}
                      </Text>
                      <Icon
                        as={FiChevronDown}
                        transform={
                          dropdown.isOpen ? "rotate(180deg)" : "rotate(0deg)"
                        }
                        transition="transform 0.2s"
                      />
                    </HStack>
                  </Box>

                  {dropdown.isOpen && (
                    <Box
                      position="absolute"
                      top="100%"
                      left={0}
                      right={0}
                      mt={1}
                      bg="white"
                      border="1px solid"
                      borderColor="gray.200"
                      borderRadius="md"
                      shadow="lg"
                      zIndex={10}
                      maxH="300px"
                      overflow="hidden"
                    >
                      <Box
                        p={2}
                        borderBottom="1px solid"
                        borderColor="gray.200"
                      >
                        <HStack>
                          <Icon as={FiSearch} color="gray.400" />
                          <Input
                            placeholder="Search variants..."
                            value={dropdown.searchQuery}
                            onChange={(e) =>
                              onDropdownChange(path.id, {
                                ...dropdown,
                                searchQuery: e.target.value
                              })
                            }
                            size="sm"
                            border="none"
                            _focus={{
                              outline: "none",
                              boxShadow: "none"
                            }}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </HStack>
                      </Box>

                      <Box maxH="250px" overflowY="auto">
                        {filteredVariants.map((variant) => (
                          <Box
                            key={variant.value}
                            as="button"
                            w="100%"
                            p={2}
                            textAlign="left"
                            bg={
                              path.value === variant.value ? "blue.50" : "white"
                            }
                            _hover={{ bg: "gray.50" }}
                            onClick={(e) => {
                              e.stopPropagation();
                              updatePath(index, { value: variant.value });
                              onDropdownChange(path.id, {
                                isOpen: false,
                                searchQuery: ""
                              });
                            }}
                            borderBottom="1px solid"
                            borderColor="gray.100"
                          >
                            <HStack justify="space-between" align="start">
                              <VStack align="start" gap={0}>
                                <Text
                                  fontSize="sm"
                                  fontWeight={
                                    path.value === variant.value
                                      ? "medium"
                                      : "normal"
                                  }
                                  fontFamily="mono"
                                >
                                  {variant.label}
                                </Text>
                                <Text fontSize="xs" color="gray.500">
                                  {variant.description}
                                </Text>
                              </VStack>
                              {path.value === variant.value && (
                                <Icon as={FiCheck} color="blue.500" />
                              )}
                            </HStack>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  )}
                  </Box>
                  <Button
                    size="xs"
                    variant="ghost"
                    onClick={() => {
                      const refreshKey = `${path.id}-variant`;
                      setRefreshingStates(prev => ({ ...prev, [refreshKey]: true }));
                      // Reset search and close dropdown to refresh the list
                      onDropdownChange(path.id, {
                        isOpen: false,
                        searchQuery: ""
                      });
                      // Small delay then reopen to show refreshed list
                      setTimeout(() => {
                        onDropdownChange(path.id, {
                          isOpen: true,
                          searchQuery: ""
                        });
                        setRefreshingStates(prev => ({ ...prev, [refreshKey]: false }));
                      }, 500);
                    }}
                    disabled={disabled || refreshingStates[`${path.id}-variant`]}
                    aria-label="Refresh variant list"
                    title="Refresh variant list"
                    px={2}
                    _hover={{ bg: "gray.100" }}
                  >
                    <Icon 
                      as={FiRefreshCw} 
                      boxSize={3} 
                      className={refreshingStates[`${path.id}-variant`] ? "animate-spin" : ""} 
                    />
                  </Button>
                </HStack>
              )}

              <HStack mt={2}>
                <Text fontSize="xs" color="gray.600">
                  Traffic:
                </Text>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  value={path.percentage}
                  onChange={(e) =>
                    updatePath(index, {
                      percentage: parseInt(e.target.value) || 0
                    })
                  }
                  size="xs"
                  width="60px"
                  disabled={disabled}
                />
                <Text fontSize="xs" color="gray.600">
                  %
                </Text>
              </HStack>
            </Box>
          );
        })}
      </VStack>

      <Box mt={3}>
        <HStack justify="space-between" mb={2}>
          <Text fontSize="xs" color="gray.600">
            Total: {totalPercentage}%
          </Text>
          <Button
            type="button"
            size="xs"
            variant="outline"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              distributeEvenly();
            }}
            disabled={disabled}
          >
            Distribute Evenly
          </Button>
        </HStack>
        {totalPercentage !== 100 && (
          <Text fontSize="xs" color="red.500">
            Warning: Traffic distribution must equal 100%
          </Text>
        )}
      </Box>
    </Box>
  );
};

// Add spinning animation
if (typeof document !== 'undefined' && !document.querySelector('#ab-test-paths-styles')) {
  const style = document.createElement("style");
  style.id = 'ab-test-paths-styles';
  style.textContent = `
    @keyframes spin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }
    .animate-spin {
      animation: spin 1s linear infinite;
    }
  `;
  document.head.appendChild(style);
}
